/**
 * Estadisticas Turnos - Turn statistics dashboard component
 */

import { useTranslation } from "react-i18next";
import { Clock, Users, CheckCircle, XCircle, TrendingUp, AlertCircle } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";

import type { TurnoEstadisticas } from "@/hooks/use-turnos";

interface EstadisticasTurnosProps {
  estadisticas: TurnoEstadisticas | null;
  isLoading?: boolean;
  error?: string | null;
}

export function EstadisticasTurnos({
  estadisticas,
  isLoading = false,
  error = null,
}: EstadisticasTurnosProps) {
  const { t } = useTranslation();

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="flex items-center justify-center py-8">
          <div className="text-center text-destructive">
            <AlertCircle className="h-8 w-8 mx-auto mb-2" />
            <p>{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <Skeleton className="h-4 w-24" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!estadisticas) {
    return null;
  }

  const { total, por_estado, por_prioridad, tiempo_espera_promedio, tiempo_atencion_promedio } =
    estadisticas;

  // Calculate percentages - backend returns lowercase keys
  const atendidos = por_estado?.atendidos || por_estado?.ATENDIDO || 0;
  const cancelados = por_estado?.cancelados || por_estado?.CANCELADO || 0;
  const esperando = por_estado?.esperando || por_estado?.ESPERANDO || 0;
  const enAtencion = por_estado?.en_atencion || por_estado?.EN_ATENCION || 0;

  const tasaAtencion = total > 0 ? Math.round((atendidos / total) * 100) : 0;
  const tasaCancelacion = total > 0 ? Math.round((cancelados / total) * 100) : 0;

  // Priority distribution - backend returns lowercase keys
  const prioridadAlta = por_prioridad?.alta || por_prioridad?.ALTA || 0;
  const prioridadMedia = por_prioridad?.media || por_prioridad?.MEDIA || 0;
  const prioridadBaja = por_prioridad?.baja || por_prioridad?.BAJA || 0;

  return (
    <div className="space-y-4">
      {/* Main Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Total Turns */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("turnos.stats.total", "Total Turnos")}
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{total}</div>
            <p className="text-xs text-muted-foreground">
              {esperando} {t("turnos.stats.waiting", "esperando")}, {enAtencion}{" "}
              {t("turnos.stats.inProgress", "en atención")}
            </p>
          </CardContent>
        </Card>

        {/* Attended */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("turnos.stats.attended", "Atendidos")}
            </CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{atendidos}</div>
            <div className="flex items-center gap-2">
              <Progress value={tasaAtencion} className="h-2" />
              <span className="text-xs text-muted-foreground">{tasaAtencion}%</span>
            </div>
          </CardContent>
        </Card>

        {/* Cancelled */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("turnos.stats.cancelled", "Cancelados")}
            </CardTitle>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{cancelados}</div>
            <div className="flex items-center gap-2">
              <Progress value={tasaCancelacion} className="h-2 [&>div]:bg-red-500" />
              <span className="text-xs text-muted-foreground">{tasaCancelacion}%</span>
            </div>
          </CardContent>
        </Card>

        {/* Average Wait Time */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("turnos.stats.avgWait", "Tiempo Espera Prom.")}
            </CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {tiempo_espera_promedio !== undefined ? `${tiempo_espera_promedio} min` : "-"}
            </div>
            {tiempo_atencion_promedio !== undefined && (
              <p className="text-xs text-muted-foreground">
                {t("turnos.stats.avgAttention", "Atención")}: {tiempo_atencion_promedio} min
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Priority Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            {t("turnos.stats.byPriority", "Distribución por Prioridad")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* High Priority */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full bg-red-500" />
                  <span>{t("turnos.priority.high", "Alta")}</span>
                </div>
                <span className="font-medium">{prioridadAlta}</span>
              </div>
              <Progress
                value={total > 0 ? (prioridadAlta / total) * 100 : 0}
                className="h-2 [&>div]:bg-red-500"
              />
            </div>

            {/* Medium Priority */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full bg-yellow-500" />
                  <span>{t("turnos.priority.medium", "Media")}</span>
                </div>
                <span className="font-medium">{prioridadMedia}</span>
              </div>
              <Progress
                value={total > 0 ? (prioridadMedia / total) * 100 : 0}
                className="h-2 [&>div]:bg-yellow-500"
              />
            </div>

            {/* Low Priority */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full bg-green-500" />
                  <span>{t("turnos.priority.low", "Baja")}</span>
                </div>
                <span className="font-medium">{prioridadBaja}</span>
              </div>
              <Progress
                value={total > 0 ? (prioridadBaja / total) * 100 : 0}
                className="h-2 [&>div]:bg-green-500"
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
